<script setup lang="ts">
import type { AssociationPickerOption } from "@/composition/useAssociationOptions";
import {
  CheckIcon,
  ChevronDownIcon,
  PlusIcon,
  SearchLgIcon,
} from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UIDivider } from "@gohighlevel/ghl-ui";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

interface Props {
  modelValue?: string;
  options: AssociationPickerOption[];
  loading?: boolean;
  error?: string;
  placeholder?: string;
  allowCreate?: boolean;
  createLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  loading: false,
  error: "",
  placeholder: "",
  allowCreate: false,
  createLabel: "",
});

const emit = defineEmits<{
  "update:modelValue": [value: string];
  create: [];
}>();

const isOpen = ref(false);
const rootRef = ref<HTMLElement | null>(null);

const selectedOption = computed(() =>
  props.options.find((option) => option.value === props.modelValue)
);

const isDisabled = computed(() => props.loading || !!props.error);

const selectOption = (option: AssociationPickerOption) => {
  emit("update:modelValue", option.value);
  isOpen.value = false;
};

const closeOnOutside = (event: MouseEvent) => {
  if (rootRef.value && !rootRef.value.contains(event.target as Node)) {
    isOpen.value = false;
  }
};

onMounted(() => document.addEventListener("click", closeOnOutside));
onUnmounted(() => document.removeEventListener("click", closeOnOutside));
</script>

<template>
  <div ref="rootRef" class="relative">
    <button
      type="button"
      class="relative w-full cursor-pointer rounded-lg border border-gray-300 bg-white py-3 pl-3 pr-10 text-left shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50"
      :disabled="isDisabled"
      :aria-expanded="isOpen"
      :aria-haspopup="true"
      aria-controls="association-select-listbox"
      role="combobox"
      :aria-label="t('common.associations')"
      @click="isOpen = !isOpen"
    >
      <div v-if="selectedOption" class="block">
        <div class="text-sm font-medium text-gray-900">{{ selectedOption.label }}</div>
        <div class="mt-1 text-sm text-gray-500">{{ selectedOption.description }}</div>
      </div>
      <div v-else class="block text-gray-500">
        {{ placeholder || t("common.selectAnAssociation") }}
      </div>
      <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
        <ChevronDownIcon class="h-5 w-5 text-gray-400" />
      </span>
    </button>

    <div
      v-if="isOpen && !isDisabled"
      id="association-select-listbox"
      class="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
      role="listbox"
    >
      <!-- Empty state -->
      <div v-if="options.length === 0" class="px-2 py-4 text-center">
        <div
          class="mx-auto mb-3 flex h-8 w-8 items-center justify-center rounded-full bg-primary-100"
        >
          <SearchLgIcon class="h-4 w-4 text-primary-600" />
        </div>
        <div class="mb-2 text-sm font-medium text-gray-900">
          {{ t("common.noAssociationsFound") }}
        </div>
      </div>

      <!-- Options -->
      <div
        v-for="option in options"
        v-else
        :key="option.value"
        class="relative flex cursor-pointer select-none items-center justify-between px-3 py-3 transition-colors hover:bg-gray-50"
        :class="{ 'bg-gray-50': option.value === modelValue }"
        role="option"
        tabindex="0"
        :aria-selected="option.value === modelValue"
        @click="selectOption(option)"
        @keydown.enter.prevent="selectOption(option)"
        @keydown.space.prevent="selectOption(option)"
      >
        <div class="block">
          <div
            class="text-sm font-medium"
            :class="option.value === modelValue ? 'text-primary-600' : 'text-gray-900'"
          >
            {{ option.label }}
          </div>
          <div class="mt-1 text-sm text-gray-500">{{ option.description }}</div>
        </div>
        <CheckIcon
          v-if="option.value === modelValue"
          class="ml-2 h-5 w-5 shrink-0 text-primary-600"
        />
      </div>

      <!-- Create new action -->
      <template v-if="allowCreate">
        <UIDivider />
        <div class="px-2 pb-1">
          <UIButton id="create-new-association" text type="primary" @click="emit('create')">
            <PlusIcon class="mr-2 h-4 w-4" />
            {{ createLabel || t("common.createNewAssociation") }}
          </UIButton>
        </div>
      </template>
    </div>

    <div v-if="error" class="mt-1 text-xs text-red-500">{{ error }}</div>
    <div v-if="loading" class="mt-1 text-xs text-gray-500" role="status" aria-live="polite">
      {{ t("common.loadingAssociations") }}
    </div>
  </div>
</template>

<style scoped>
/* UIDivider does not forward class to its root, so override naive-ui defaults here. */
:deep(.n-divider:not(.n-divider--vertical)) {
  margin-top: 0;
  margin-bottom: 16px;
}
</style>
